import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase/config";

interface ClientInfoResponse {
    clientName: string;
}

interface CompleteAuthorizationResponse {
    redirectUrl: string;
}

const getOAuthClientInfo = httpsCallable<{ client_id: string }, ClientInfoResponse>(functions, "getOAuthClientInfo");
const completeOAuthAuthorization = httpsCallable<Record<string, unknown>, CompleteAuthorizationResponse>(functions, "completeOAuthAuthorization");

const Spinner: React.FC<{ className?: string }> = ({ className = "size-8 text-gray-900" }) => (
    <svg className={`animate-spin ${className}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
);

/**
 * The OAuth consent page. A third-party app (Claude, ChatGPT, etc.) sends the
 * user here (already authenticated, via PrivateRoute) with client_id/redirect_uri/PKCE
 * params to ask for access to their Peak account. Every actual decision (does this
 * client_id/redirect_uri pair exist, is the code_challenge valid) is re-validated
 * server-side in completeOAuthAuthorization — this page only does presence checks
 * so it can fail fast with a friendly message.
 */
const OAuthPage: React.FC = () => {
    const [searchParams] = useSearchParams();

    const [invalidRequest, setInvalidRequest] = useState<string | null>(null);
    const [loadingClient, setLoadingClient] = useState(true);
    const [clientName, setClientName] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const client_id = searchParams.get("client_id") ?? "";
    const redirect_uri = searchParams.get("redirect_uri") ?? "";
    const code_challenge = searchParams.get("code_challenge") ?? "";
    const code_challenge_method = searchParams.get("code_challenge_method") ?? "";
    const state = searchParams.get("state") ?? "";
    const scope = searchParams.get("scope") ?? "";

    useEffect(() => {
        const response_type = searchParams.get("response_type") ?? "";

        if (!client_id || !redirect_uri) {
            setInvalidRequest("This link is missing required parameters. Please restart the connection from the app you were setting up.");
            setLoadingClient(false);
            return;
        }

        if (response_type !== "code" || code_challenge_method !== "S256" || !code_challenge) {
            setInvalidRequest("This app is using an authorization method Peak doesn't support. Please make sure it's up to date.");
            setLoadingClient(false);
            return;
        }

        getOAuthClientInfo({ client_id })
            .then((result) => {
                setClientName(result.data.clientName);
                setLoadingClient(false);
            })
            .catch((err: unknown) => {
                console.error("getOAuthClientInfo failed:", err);
                const code = (err as { code?: string })?.code;
                setInvalidRequest(
                    code === "functions/not-found"
                        ? "This application isn't registered with Peak. Please try connecting again from the app."
                        : "Something went wrong loading this request. Please try again in a moment."
                );
                setLoadingClient(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const respond = async (approved: boolean) => {
        setSubmitting(true);
        setError(null);

        try {
            const result = await completeOAuthAuthorization({
                client_id,
                redirect_uri,
                code_challenge,
                code_challenge_method,
                state,
                scope,
                approved,
            });
            window.location.href = result.data.redirectUrl;
        } catch (err) {
            console.error("completeOAuthAuthorization failed:", err);
            setSubmitting(false);
            setError("Something went wrong. Please try again.");
        }
    };

    return (
        <section className="min-h-screen w-full flex items-center justify-center p-6 bg-gray-50">
            <div className="w-full max-w-md">
                {invalidRequest ? (
                    <div className="bg-white rounded-2xl border shadow p-8 flex flex-col items-center gap-3 text-center">
                        <span className="text-2xl font-black font-grotesk">
                            PEAK<span className="font-light text-gray-500">^</span>
                        </span>
                        <h1 className="text-xl font-semibold text-gray-900">Can't continue</h1>
                        <p className="text-gray-500 text-sm">{invalidRequest}</p>
                    </div>
                ) : loadingClient ? (
                    <div className="flex items-center justify-center py-20">
                        <Spinner />
                    </div>
                ) : (
                    <div className="bg-white rounded-2xl border shadow p-8 flex flex-col gap-6">
                        <div className="flex flex-col items-center gap-3 text-center">
                            <span className="text-2xl font-black font-grotesk">
                                PEAK<span className="font-light text-gray-500">^</span>
                            </span>
                            <h1 className="text-xl font-semibold text-gray-900">{clientName} wants to connect to your Peak account</h1>
                            <p className="text-gray-500 text-sm">This will let it search and query the data you're subscribed to, on your behalf.</p>
                        </div>

                        {error && <p className="text-red-500 text-sm text-center">{error}</p>}

                        <div className="flex flex-col gap-3">
                            <button
                                type="button"
                                className="btn !border-none w-full h-14 !bg-gray-900 !text-white !font-grotesk hover:!bg-gray-800"
                                disabled={submitting}
                                onClick={() => respond(true)}
                            >
                                {submitting ? <Spinner className="size-5 text-white" /> : <span className="font-medium">Approve</span>}
                            </button>
                            <button
                                type="button"
                                className="btn bg-transparent shadow-none w-full h-14"
                                disabled={submitting}
                                onClick={() => respond(false)}
                            >
                                Deny
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
};

export default OAuthPage;
